import { useContext, useEffect, useRef, useState } from 'react';
import { differenceInSeconds, parseISO } from 'date-fns';
import { Slider, Stack } from '@mui/material';
import { ActionDialogProps } from '@/components/ActionsDialog/types';
import { MTDialog } from '@/components/common/MTDialog';
import { AudioRecordsContext } from '@/contexts/audioRecords/context';
import { COMMON_STRING } from '@/constants/dictionary';
import { generateSubTitleDialog } from '@/utils/dialogSubTitle/generateSubTitleDialog';
import { ScoringContext } from '@/contexts/scoring/context';
import { StreamingVolumeSlider } from '../StreamingDialog/StreamingVolumeSlider';
import { AudioPlayerButtons } from './AudioPlayerButtons';

export const JumpToAudioDialog = ({ action, onClose }: ActionDialogProps) => {
  const { records } = useContext(AudioRecordsContext);
  const {
    state: { actionTypesCurrentSport },
  } = useContext(ScoringContext);

  const currentPeriodRecordsByScorer = records.find(
    (record) =>
      record.periodNo === action.period && record.scorerId === action.scorerId,
  );

  const audioRef = useRef<HTMLAudioElement>(null);
  const [clipStart, setClipStart] = useState(0);
  const [clipEnd, setClipEnd] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(1);
  const clipLength = clipEnd - clipStart;

  useEffect(() => {
    if (!currentPeriodRecordsByScorer) {
      return;
    }

    const recordToStart = parseISO(currentPeriodRecordsByScorer.timestampUtc);
    const actionTime = parseISO(action.timestamp);
    const secondsOffset = differenceInSeconds(actionTime, recordToStart);
    const start = Math.max(0, secondsOffset - 5);
    const end = Math.floor(secondsOffset + 5);

    setClipStart(start);
    setClipEnd(end);
    setProgress(0);
  }, [action.scorerId, action.timestamp, currentPeriodRecordsByScorer]);

  useEffect(() => {
    const audio = audioRef.current;

    if (!audio) return;

    const onLoadedMetadata = () => {
      const duration = audio.duration;
      const endFixed = Math.min(Math.floor(duration), clipEnd);
      const startFixed = Math.min(clipStart, endFixed - 1);

      audio.currentTime = startFixed;
      setClipEnd(endFixed);
      setClipStart(startFixed);
    };

    audio.currentTime = clipStart;
    audio.play();

    const onTimeUpdate = () => {
      if (audio.currentTime >= clipEnd) {
        audio.currentTime = clipStart;
        audio.play();
      } else {
        setProgress(audio.currentTime - clipStart);
      }
    };

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
    };
  }, [clipStart, clipEnd]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (playing) {
      setProgress(audio.currentTime - clipStart);

      audio.pause();
    } else {
      if (audio.currentTime < clipStart || audio.currentTime > clipEnd) {
        audio.currentTime = clipStart;
      }
      setProgress(audio.currentTime - clipStart);
      audio.play();
    }
    setPlaying(!playing);
  };

  const onClickVolume = (newVolume: number) => {
    const audio = audioRef.current;
    if (!audio) return;

    const value = Array.isArray(newVolume) ? newVolume[0] : newVolume;
    audio.volume = value;

    setVolume(value);
  };

  const onChangeTimeline = (event: Event, newValue: number | number[]) => {
    const audio = audioRef.current;
    if (!audio) return;
    const newTime =
      clipStart + (Array.isArray(newValue) ? newValue[0] : newValue);

    const timeToShow = newTime >= clipEnd ? clipStart : newTime;

    audio.currentTime = timeToShow;
    setProgress(newTime - clipStart);
  };

  return (
    <MTDialog
      open={true}
      title={COMMON_STRING.ACTION_AUDIO}
      onClose={onClose}
      maxWidth='xs'
      fullWidth
    >
      <Stack width='100%'>
        {generateSubTitleDialog({ action, actionTypesCurrentSport })}
        {currentPeriodRecordsByScorer && (
          <Stack>
            <audio
              ref={audioRef}
              onVolumeChange={() => onClickVolume}
              src={currentPeriodRecordsByScorer?.url ?? ''}
              preload='metadata'
            />

            <Slider
              aria-label={COMMON_STRING.TIMELINE}
              value={Math.floor(progress)}
              step={1}
              min={0}
              max={clipLength}
              onChange={onChangeTimeline}
            />
            <Stack direction='row' justifyContent='space-between'>
              <AudioPlayerButtons
                playing={playing}
                togglePlay={togglePlay}
                progress={progress}
                clipLength={clipLength}
              />
              <StreamingVolumeSlider
                volume={volume}
                onClickVolume={onClickVolume}
                props={{ width: '50%' }}
              />
            </Stack>
          </Stack>
        )}
      </Stack>
    </MTDialog>
  );
};
